import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Layout, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getIdentityVerification,
  VerifyIdentitiesBlock,
} from "features/verify-identities";
import { useNavigate } from "react-router-dom";
import ROUTES from "shared/routes";
import styles from "./index.module.scss";
import { AuthHeader } from "widgets/headers";
import { getProfile } from "entities/viewer";

type IdentityVerificationProps = {
  isMain?: boolean;
};

export const IdentityVerificationPage = ({
  isMain,
}: IdentityVerificationProps) => {
  if (isMain) {
    return <IdentityVerificationPageContent isMain />;
  }

  return (
    <Layout>
      <AuthHeader />
      <Layout.Content
        style={{
          paddingTop: "13vh",
        }}
        className="auth-layout-content"
      >
        <IdentityVerificationPageContent />
      </Layout.Content>
    </Layout>
  );
};

const IdentityVerificationPageContent = ({
  isMain,
}: IdentityVerificationProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: verificationData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["verification-data"],
    queryFn: () => getIdentityVerification(),
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      return query.state.data?.data?.status === "in_progress" ? 5000 : false;
    },
    gcTime: 0,
    staleTime: 0,
  });

  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => getProfile(),
  });

  const goNext = () => {
    if (profile?.need_sign_personal_data) {
      navigate(ROUTES.SIGN_PERSONAL_DATA);
    } else {
      const pathname = localStorage.getItem("pathname");
      if (pathname) {
        navigate(pathname);
      } else {
        navigate(ROUTES.MAIN);
      }
    }
  };

  return (
    <>
      <Typography.Title level={3}>
        {!!isMain && (
          <Button
            type="link"
            size="large"
            icon={
              <ArrowLeftOutlined style={{ fontSize: "20px", marginRight: 8 }} />
            }
            onClick={() => navigate(ROUTES.MAIN)}
          ></Button>
        )}
        {t("identity_verification.title")}
      </Typography.Title>
      <Typography.Text
        style={{
          fontSize: 16,
          marginBottom: 24,
          display: "block",
          color: "var(--color-grey)",
        }}
      >
        {t("identity_verification.subtitle")}
      </Typography.Text>

      {isLoading ? (
        <div className={styles.wrapper}>
          <Spin />
        </div>
      ) : isError || !verificationData?.data ? (
        <>
          <Typography.Text
            style={{
              fontSize: 14,
              marginBottom: 16,
              display: "block",
              color: "var(--color-grey)",
            }}
          >
            {t("identity_verification.error")} {error?.message}
          </Typography.Text>
          <Button
            type="primary"
            onClick={() =>
              queryClient.invalidateQueries({
                queryKey: ["verification-data"],
              })
            }
            block
          >
            {t("identity_verification.retry")}
          </Button>
        </>
      ) : (
        <VerifyIdentitiesBlock
          verificationData={verificationData.data}
          goNext={!isMain ? goNext : undefined}
          isMain={isMain}
          profileInn={profile?.inn}
        />
      )}

      {!isMain && (
        <Button
          block
          type="text"
          style={{ marginTop: 24 }}
          onClick={() => navigate(ROUTES.LOGIN)}
        >
          {t("identity_verification.change_number")}
        </Button>
      )}
    </>
  );
};
